import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard, Users, Target, Building2, Calendar, Receipt,
  Calculator, BarChart3, FileText, LogOut, Box, ChevronDown, Activity, Settings, AlertTriangle, CreditCard, Globe, Menu, X, TrendingUp, HeartPulse
} from "lucide-react";
import { useEffect, useState } from "react";
import TrialBanner from "../TrialBanner";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fecha drawer ao trocar de rota
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Bloqueia scroll do body enquanto drawer está aberto
  useEffect(() => {
    document.body.classList.toggle("scroll-locked", drawerOpen);
    return () => document.body.classList.remove("scroll-locked");
  }, [drawerOpen]);

  // Fecha drawer com tecla ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // === RBAC ===
  // Owner = super_admin (Emergent's top role) — vê tudo
  // Admin = admin — vê tudo EXCETO Super Admin (SaaS Metrics)
  // Gerente = manager — opera clientes/reservas/espaços, sem config crítica
  // Operador = operator — apenas operacional básico (reservas, clientes, espaços read)
  const role = user?.role;
  const isOwner = role === "super_admin";
  const isAdmin = ["super_admin", "admin"].includes(role);
  const isManagerUp = ["super_admin", "admin", "manager"].includes(role);
  const isStaff = ["super_admin", "admin", "manager", "operator", "reception", "commercial", "financial"].includes(role);

  const groups = [
    {
      title: "Visão Geral",
      items: [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, show: isStaff },
        { to: "/admin/metrics", label: "Métricas", icon: TrendingUp, show: isManagerUp },
      ]
    },
    ...(isOwner ? [{
      title: "Super Admin",
      items: [
        { to: "/admin/saas-admin", label: "SaaS Metrics", icon: Globe, show: true },
      ]
    }] : []),
    {
      title: "Comercial",
      items: [
        { to: "/admin/leads", label: "Leads (CRM)", icon: Target, show: isStaff },
        { to: "/admin/leads-sla", label: "SLA & Atendimento", icon: AlertTriangle, show: isManagerUp },
      ]
    },
    {
      title: "Equipe",
      items: [
        { to: "/admin/usuarios", label: "Funcionários", icon: Users, show: isAdmin },
        { to: "/admin/clientes", label: "Clientes", icon: Users, show: isStaff },
      ]
    },
    {
      title: "Operacional",
      items: [
        { to: "/admin/espacos", label: "Espaços", icon: Building2, show: isStaff },
        { to: "/admin/reservas", label: "Reservas", icon: Calendar, show: isStaff },
        { to: "/admin/planos", label: "Planos", icon: Box, show: isAdmin },
      ]
    },
    {
      title: "Financeiro",
      items: [
        { to: "/admin/financeiro", label: "Faturas", icon: Receipt, show: isManagerUp },
        { to: "/admin/contratos", label: "Contratos", icon: FileText, show: isManagerUp },
        { to: "/admin/precificacao", label: "Precificação", icon: Calculator, show: isAdmin },
        { to: "/admin/relatorios", label: "Relatórios", icon: BarChart3, show: isManagerUp },
      ]
    },
    {
      title: "Sistema",
      items: [
        { to: "/admin/unidades", label: "Unidades", icon: Building2, show: isAdmin },
        { to: "/admin/billing", label: "Plano & Assinatura", icon: CreditCard, show: isAdmin },
        { to: "/admin/configuracoes", label: "Configurações", icon: Settings, show: isAdmin },
        { to: "/admin/auditoria", label: "Auditoria", icon: Activity, show: isAdmin },
        { to: "/admin/system-health", label: "Saúde do Sistema", icon: HeartPulse, show: isAdmin },
      ]
    },
  ]
    // Filter items and drop entire groups if nothing left to show
    .map(g => ({ ...g, items: g.items.filter(i => i.show !== false) }))
    .filter(g => g.items.length > 0);

  const renderSidebar = (scope) => (
    <>
      <div className="px-5 py-5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#14213D] flex items-center justify-center">
            <span className="font-display font-bold text-[#D4AF37] text-sm">VM</span>
          </div>
          <div>
            <div className="font-display font-semibold text-[#14213D] text-sm">VM Corporate</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Painel Admin</div>
          </div>
        </div>
        {scope === "mobile" && (
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
            aria-label="Fechar menu"
            data-testid="admin-drawer-close"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-semibold px-3 mb-2">{g.title}</div>
            <div className="space-y-1">
              {g.items.map((it) => {
                const slug = it.to.split("/").pop() || "dashboard";
                const baseTid = `admin-nav-${slug}`;
                const tid = scope === "mobile" ? `${baseTid}-m` : baseTid;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`}
                    data-testid={tid}
                  >
                    <it.icon size={16} />
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-gray-100 p-3">
        <button
          onClick={async () => { setDrawerOpen(false); await logout(); navigate("/"); }}
          className="sidebar-nav-item w-full text-left"
          data-testid={scope === "mobile" ? "admin-logout-btn-m" : "admin-logout-btn"}
        >
          <LogOut size={16} /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex" data-testid="admin-layout">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-col fixed h-full z-30">
        {renderSidebar("desktop")}
      </aside>

      {/* Drawer mobile overlay */}
      {drawerOpen && (
        <div
          className="mobile-drawer-overlay lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="admin-drawer-overlay"
        />
      )}

      {/* Sidebar mobile (drawer) */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-white border-r border-gray-200 flex flex-col z-50 transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="admin-drawer"
        aria-hidden={!drawerOpen}
      >
        {renderSidebar("mobile")}
      </aside>

      {/* Conteúdo */}
      <div className="flex-1 w-full lg:ml-64 min-w-0">
        {/* Topbar mobile com hamburger */}
        <div className="mobile-topbar lg:hidden">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-md hover:bg-gray-100 text-[#14213D]"
            aria-label="Abrir menu"
            data-testid="admin-drawer-open"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#14213D] flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-[11px]">VM</span>
            </div>
            <span className="font-display font-semibold text-[#14213D] text-sm">VM Corporate</span>
          </div>
          <div className="w-8 h-8 rounded-full bg-[#14213D] text-white flex items-center justify-center text-xs font-semibold" title={user?.name}>
            {user?.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
          </div>
        </div>

        <TrialBanner />

        {/* Header desktop */}
        <header className="hidden lg:flex h-16 bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 items-center justify-between sticky top-0 z-30">
          <div className="text-sm text-gray-500 truncate">
            <span>Bem-vindo,</span> <span className="text-[#14213D] font-semibold">{user?.name}</span>
            <span className="ml-2 inline-block px-2 py-0.5 bg-[#D4AF37]/15 text-[#7a6519] rounded text-[10px] uppercase tracking-wider font-semibold">{user?.role}</span>
          </div>
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-3 py-1.5"
              data-testid="admin-profile-menu"
            >
              <div className="w-8 h-8 rounded-full bg-[#14213D] text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
              </div>
              <ChevronDown size={14} />
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-12 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="font-semibold text-sm">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
                <button
                  onClick={() => { setProfileOpen(false); navigate("/meu-perfil"); }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-gray-700"
                  data-testid="admin-menu-profile"
                >
                  <Settings size={14} /> Meu Perfil
                </button>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 text-red-600"
                  data-testid="admin-menu-logout"
                >
                  <LogOut size={14} /> Sair
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 admin-content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
